var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-multiple-image-picker.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import "@adornis/base/utilTypes.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { event } from "@adornis/chemistry/decorators/event.js";
import { acss } from "@adornis/chemistry/directives/acss.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import "@adornis/chemistry/elements/components/x-loader.js";
import "@adornis/dragdrop/x-drag-drop.js";
import { FormField } from "@adornis/formfield/form-field.js";
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { BehaviorSubject, firstValueFrom, timer } from "rxjs";
import { filter, pairwise, startWith, switchMap, takeUntil } from "rxjs/operators";
import { ImageFile } from "../db/images.js";
import { askForFile } from "./helper-methods.js";
import "./x-image.js";
var XMultipleImagePicker = class extends FormField {
  constructor() {
    super(...arguments);
    this.imageFiles = new RXController(this, []);
    this.max = 0;
    this.clearIfValNull = false;
    this.addedAmount = 0;
    this.enableUpload = new RXController(this, false);
    // @event geotag = new BehaviorSubject<[number, number] | undefined>(undefined);
    this.imagesToDelete = [];
    this.fileEntity = ImageFile;
    this.progress = new BehaviorSubject(0);
    this.delete = new RXController(this, false);
  }
  async preValidate() {
    if (this.imageFiles.value.length <= 0)
      return;
    const ids = await Promise.all(
      this.imageFiles.value.map(async (item) => {
        if (item.type === "id")
          return item.val;
        const uploadResult = await this.fileEntity.upload(item.val);
        return uploadResult;
      })
    );
    return this.value.next(ids);
  }
  async deleteImages() {
    if (this.imagesToDelete.length > 0) {
      for (let i = 0; i < this.imagesToDelete.length; i++) {
        await this.fileEntity.removeFile(this.imagesToDelete[i])().catch((err) => {
          logger.error({ err }, "Error while deleting an image file.");
        });
      }
    }
    this.imagesToDelete = [];
  }
  async postValidate(errored) {
    if (errored)
      return;
    this.imageFiles.next([]);
    this.enableUpload.next(false);
    this.delete.next(false);
    await this.deleteImages();
  }
  connectedCallback() {
    super.connectedCallback();
    this.value.observable.pipe(
      // take(1),
      takeUntil(this.disconnected)
    ).subscribe((val) => {
      if (!val || val.length === 0) {
        if (this.clearIfValNull) {
          return this.imageFiles.next([]);
        }
        return;
      }
      const initValues = [];
      for (let i = 0; i < val.length; i++) {
        initValues.push({ val: val[i], type: "id" });
      }
      this.imageFiles.next(initValues);
    });
  }
  render() {
    return html`
      <x-drag-drop
        child-selector="div"
        allow-from=".handle"
        @resorted="${(e) => {
      const movedImageID = this.imageFiles.value?.splice(e.detail.start, 1)[0];
      if (!movedImageID)
        throw new Error("movedImageID should exist (x-multiple-image-picker");
      const val = this.imageFiles.value;
      val.splice(e.detail.end, 0, movedImageID);
      this.imageFiles.next(val);
    }}"
      >
        ${this._imagePickerWrapper()}
      </x-drag-drop>
    `;
  }
  _imagePickerWrapper() {
    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="md"
        ${css({
      width: "100vw",
      marginLeft: `-${this.spacing.md}`,
      marginRight: `-${this.spacing.md}`,
      paddingLeft: this.spacing.md
    })}
        @drop=${(e) => {
      e.preventDefault();
      const dt = e.dataTransfer;
      if (!dt)
        throw new Error("data transfer should exist (x-multiple-image-picker)");
      const files = dt.files;
      void this.handleSelectedFiles(files);
    }}
      >
        ${this._loadImages()} ${this._addImagesButton()}
      </x-flex>
    `;
  }
  _addImagesButton() {
    return html`
      <div
        ${acss({
      flex: "none",
      height: `110px`,
      width: "110px",
      border: `1px solid white`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      borderRadius: this.sizes.borderRadius,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: this.colors.primary,
      transition: "background .2s",
      cursor: "pointer",
      position: "relative",
      "&:hover": {
        background: "rgba(0, 0, 0, .3)"
      }
    })}
        @click=${async () => {
      const files = await askForFile({ acceptFileTypes: ".jpg,.jpeg,.png,.webp", acceptMultipleFiles: true });
      if (!files)
        return;
      await this.handleSelectedFiles(files);
    }}
      >
        <x-icon ${css({ fontSize: "21px", color: this.colors.secondary })}> camera_alt </x-icon>

        <div
          ${css({
      position: "absolute",
      bottom: "0",
      left: "0",
      width: `calc(${this.progress.value} * 100%)`,
      height: "4px",
      background: this.colors.accent,
      transition: "width .3s"
    })}
        ></div>
      </div>
    `;
  }
  async handleSelectedFiles(fileList) {
    const fileArray = Array.from(fileList);
    if (!fileArray || fileArray.length <= 0)
      return;
    this.addedAmount = fileArray.length;
    const checkIfExceeds = (filess) => {
      if (!this.max)
        return filess;
      const currCount = this.imageFiles.value?.length || 0;
      if (currCount + filess.length < this.max)
        return filess;
      const diff = currCount + filess.length - this.max;
      return Array.from(filess).slice(0, diff * -1);
    };
    const files = [];
    for (let i = 0; i < checkIfExceeds(fileArray).length; i++) {
      const file = fileArray[i];
      files.push({ val: file, type: "file" });
      this.progress.next((i + 1) / files.length);
    }
    await firstValueFrom(timer(1e3));
    this.progress.next(0);
    this.imageFiles.next([...this.imageFiles.value, ...files]);
  }
  _loadImages() {
    return repeat(
      this.imageFiles.value || [],
      (imageFile, index) => html`
          <div ${css({ height: `110px`, width: "110px", position: "relative" })} class="handle" key="${index}">
            <image-item
              .imageFile=${imageFile}
              @delete=${() => {
        if (imageFile.type === "id") {
          this.imagesToDelete.push(imageFile.val);
          const val2 = [...this.value.value];
          val2.splice(index, 1);
          this.value.next(val2);
        }
        const val = [...this.imageFiles.value];
        val.splice(index, 1);
        this.imageFiles.next(val);
      }}
            >
            </image-item>
          </div>
        `
    );
  }
};
__decorateClass([
  state(),
  event
], XMultipleImagePicker.prototype, "imageFiles", 2);
__decorateClass([
  property({ type: Number })
], XMultipleImagePicker.prototype, "max", 2);
__decorateClass([
  property({ type: Boolean })
], XMultipleImagePicker.prototype, "clearIfValNull", 2);
__decorateClass([
  state()
], XMultipleImagePicker.prototype, "addedAmount", 2);
__decorateClass([
  state()
], XMultipleImagePicker.prototype, "enableUpload", 2);
__decorateClass([
  state()
], XMultipleImagePicker.prototype, "delete", 2);
XMultipleImagePicker = __decorateClass([
  customElement("x-multiple-image-picker")
], XMultipleImagePicker);
var ImageItem = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.imageFile = new RXController(this, void 0);
    this.fileEntity = ImageFile;
    this.image = new RXController(
      this,
      this.imageFile.observable.pipe(
        startWith({ val: "", type: "" }),
        pairwise(),
        filter(([file1, file2]) => file1?.val !== file2?.val),
        switchMap(async ([file1, file2]) => {
          if (!file2)
            return null;
          return this.readURL(file2);
        })
      )
    );
  }
  async readURL(file) {
    if (!file)
      return;
    if (file.type === "id") {
      return file;
    }
    const promise = new Promise((resolve, reject) => {
      if (A.isClient) {
        const reader = new FileReader();
        reader.onload = (e) => e.target?.result ? resolve(e.target?.result) : resolve(null);
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(file.val);
      } else
        resolve(null);
    });
    const b64 = await promise;
    if (!b64)
      throw new Error("Array Buffer should exist but ist null");
    return { val: b64, type: "file" };
  }
  _loadingState() {
    return html`
      <x-flex crossaxis-center center ${css({ height: "110px", width: "110px" })}>
        <x-loader> </x-loader>
      </x-flex>
    `;
  }
  render() {
    if (this.image.isLoading || !this.image.value)
      return this._loadingState();
    if (this.image.value.type === "id")
      return html`
        <div ${css({ height: `110px`, width: "110px", position: "relative" })}>
          <x-image
            .value=${this.image.value.val}
            .fileEntity=${this.fileEntity}
            width="110"
            height="110"
            mode="cover"
            ${css({
        flex: "none",
        height: "110px",
        width: "110px",
        borderRadius: this.sizes.borderRadius,
        overflow: "hidden"
      })}
          >
          </x-image>
          <x-icon
            ${acss({
        position: "absolute",
        // display: this.imageFiles.value.length <= 1 ? 'none' : 'flex',
        right: "0",
        top: "0",
        color: "black",
        background: this.colors.white,
        padding: this.spacing.xs,
        borderRadius: "50%",
        transition: "background .2s, color .2s",
        cursor: "pointer",
        "&:hover": { backgroundColor: this.colors.secondary, color: this.colors.primary }
      })}
            @click="${async () => {
        this.dispatchEvent(new CustomEvent("delete"));
      }}"
          >
            close
          </x-icon>
        </div>
      `;
    return html` <div
      ${css({
      flex: "none",
      height: `110px`,
      width: "110px",
      background: `url(${this.image.value.val})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      borderRadius: this.sizes.borderRadius,
      position: "relative",
      touchAction: "none"
    })}
    >
      <x-icon
        ${acss({
      position: "absolute",
      // display: this.imageFiles.value.length <= 1 ? 'none' : 'flex',
      userSelect: "none",
      right: "0",
      top: "0",
      color: "black",
      background: this.colors.white,
      padding: this.spacing.xs,
      borderRadius: "50%",
      transition: "background .2s, color .2s",
      cursor: "pointer",
      "&:hover": { background: "grey", color: "white" }
    })}
        @click="${async () => {
      this.dispatchEvent(new CustomEvent("delete"));
    }}"
      >
        close
      </x-icon>
    </div>`;
  }
};
__decorateClass([
  property()
], ImageItem.prototype, "imageFile", 2);
__decorateClass([
  state()
], ImageItem.prototype, "image", 2);
ImageItem = __decorateClass([
  customElement("image-item")
], ImageItem);
export {
  ImageItem,
  XMultipleImagePicker
};
